<!-- Сводная карточка текущего ПЛАНа или действие первого расчёта. -->
<template>
  <article v-if="currentPlan" class="plan-summary">
    <button
      class="plan-summary__toggle"
      type="button"
      :aria-expanded="expanded"
      :aria-label="expanded ? 'Свернуть детали плана и факта' : 'Показать детали плана и факта'"
      @click="$emit('toggle')"
    >
      <span class="plan-summary__badge">ПЛАН</span>
      <span class="plan-summary__main">
        <span class="plan-summary__caption">{{ resultName }}</span>
        <strong class="plan-summary__result" :class="`plan-summary__result--${resultTone}`">
          {{ formatResult(currentPlan.result) }}
        </strong>
      </span>

      <PlanCalculationSummaryMetric
        v-for="item in summaryItems"
        :key="item.code"
        :label="item.name"
        :value="formatNumber(item.value)"
      />
    </button>

    <span class="plan-summary__meta">
      <span class="plan-summary__calculator">
        <PlanCalculationCalculatorSelector
          :presets="presets"
          :current-preset-id="currentPlan.calculator_preset_id"
          :default-preset-id="defaultPresetId"
          :current-name="currentPlan.preset_snapshot?.name || 'Калькулятор'"
          :current-version="Number(currentPlan.version)"
          :changed-count="changedCount"
          :is-calculating="isCalculating"
          :readonly="!canSwitchCalculator"
          :options-loading="calculatorOptionsLoading"
          @open="$emit('request-calculators')"
          @switch="$emit('switch-calculator', $event)"
        />
        <PlanCalculationSettingsButton
          v-if="canConfigureCalculator && currentPlan.calculator_preset_id"
          :preset-id="currentPlan.calculator_preset_id"
          :calculator-name="currentPlan.preset_snapshot?.name"
        />
      </span>
      <span class="plan-summary__version">
        v{{ currentPlan.version }} · {{ formatCompactDate(currentPlan.calculated_at) }} ·
        {{ currentPlan.calculated_by?.initials || "—" }}
      </span>
    </span>

    <button
      class="plan-summary__disclosure"
      type="button"
      :aria-expanded="expanded"
      :aria-label="expanded ? 'Свернуть детали плана и факта' : 'Показать детали плана и факта'"
      @click="$emit('toggle')"
    >
      <PlanCalculationDisclosureIcon
        class="plan-summary__chevron"
        :expanded="expanded"
      />
    </button>
  </article>

  <article v-else class="plan-summary plan-summary--empty">
    <span class="plan-summary__badge">ПЛАН</span>
    <span class="plan-summary__main">
      <span class="plan-summary__caption">Плановый результат</span>
      <strong class="plan-summary__empty-value">Не рассчитан</strong>
    </span>
    <PlanCalculationActionButton
      v-if="canCalculate"
      class="plan-summary__action"
      :disabled="calculateDisabled || isCalculating"
      @click="$emit('recalculate')"
    >
      {{ isCalculating ? "Считаем..." : "Рассчитать" }}
    </PlanCalculationActionButton>
  </article>
</template>

<script>
import PlanCalculationActionButton from "@/components/Order/PlanCalculation/Buttons/Action.vue";
import PlanCalculationDisclosureIcon from "@/components/Order/PlanCalculation/Buttons/DisclosureIcon.vue";
import PlanCalculationSettingsButton from "@/components/Order/PlanCalculation/Buttons/Settings.vue";
import PlanCalculationCalculatorSelector from "@/components/Order/PlanCalculation/CalculatorSelector.vue";
import PlanCalculationSummaryMetric from "@/components/Order/PlanCalculation/SummaryMetric.vue";
import { formatBigNumber } from "@/utils/numberUtils";

export default {
  name: "OrderPlanCalculationPlanSummary",

  components: {
    PlanCalculationActionButton,
    PlanCalculationCalculatorSelector,
    PlanCalculationDisclosureIcon,
    PlanCalculationSettingsButton,
    PlanCalculationSummaryMetric,
  },

  props: {
    currentPlan: { type: Object, default: null },
    summaryItems: { type: Array, default: () => [] },
    resultName: { type: String, default: "Результат" },
    resultUnit: { type: String, default: "rub" },
    resultTone: {
      type: String,
      default: "neutral",
      validator: (value) => ["negative", "neutral", "positive"].includes(value),
    },
    expanded: { type: Boolean, default: false },
    calculateDisabled: { type: Boolean, default: false },
    isCalculating: { type: Boolean, default: false },
    presets: { type: Array, default: () => [] },
    defaultPresetId: { type: [String, Number], default: null },
    changedCount: { type: Number, default: 0 },
    canCalculate: { type: Boolean, default: false },
    canSwitchCalculator: { type: Boolean, default: false },
    canConfigureCalculator: { type: Boolean, default: false },
    calculatorOptionsLoading: { type: Boolean, default: false },
  },

  emits: ["toggle", "recalculate", "switch-calculator", "request-calculators"],

  methods: {
    formatNumber(value) {
      return formatBigNumber(value);
    },
    formatResult(value) {
      return `${formatBigNumber(value)}${this.resultUnit === "rub" ? " ₽" : ""}`;
    },
    formatCompactDate(value) {
      if (!value) return "—";
      return new Intl.DateTimeFormat("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(value));
    },
  },
};
</script>

<style scoped>
.plan-summary {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 18px;
  min-height: 64px;
  box-sizing: border-box;
  padding: 10px 14px;
  border: 1px solid #c7d2fe;
  border-radius: 10px;
  background: #fff;
  color: inherit;
  font-family: inherit;
  text-align: left;
}

.plan-summary__toggle {
  min-width: 0;
  display: flex;
  flex: 1;
  align-items: center;
  gap: 18px;
  align-self: stretch;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.plan-summary:has(.plan-summary__toggle:hover),
.plan-summary:has(.plan-summary__toggle:focus-visible),
.plan-summary:has(.plan-summary__disclosure:hover),
.plan-summary:has(.plan-summary__disclosure:focus-visible) {
  border-color: #3b5bdb;
  background: #f8f9ff;
}

.plan-summary__toggle:focus-visible,
.plan-summary__disclosure:focus-visible {
  outline: none;
}

.plan-summary__badge {
  flex: none;
  padding: 5px 10px;
  border-radius: 6px;
  background: #3b5bdb;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.8px;
}

.plan-summary__main {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.plan-summary__caption {
  color: #5b6070;
  font-size: 11px;
}

.plan-summary__result {
  font-size: 20px;
  font-weight: 700;
}

.plan-summary__result--positive {
  color: #047a69;
}
.plan-summary__result--negative {
  color: #b42318;
}
.plan-summary__empty-value {
  color: #5b6070;
}
.plan-summary__meta {
  margin-left: auto;
  display: flex;
  min-width: 0;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

.plan-summary__version {
  color: #5b6070;
  font-size: 12px;
  text-align: right;
  white-space: nowrap;
}

.plan-summary__calculator {
  display: inline-flex;
  min-width: 0;
  max-width: 100%;
  align-items: center;
  gap: 4px;
}

.plan-summary__disclosure {
  width: 24px;
  height: 32px;
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
  color: #3b5bdb;
  cursor: pointer;
}

.plan-summary__chevron {
  flex: none;
}

.plan-summary__action {
  flex: none;
  margin-left: auto;
}

@container (max-width: 620px) {
  .plan-summary {
    flex-wrap: wrap;
    gap: 10px 18px;
  }

  .plan-summary__toggle {
    flex: 1 1 calc(100% - 42px);
    flex-wrap: wrap;
    gap: 10px 18px;
  }
  .plan-summary__meta {
    order: 3;
    flex: 1 1 100%;
    align-items: flex-start;
    margin-left: 0;
  }

  .plan-summary__calculator {
    width: 100%;
  }
}
</style>
